import React, { useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import PersonAvatar from '@/components/PersonAvatar';
import { buildChatbotEntityPagePath } from '@/lib/chatbotDeepLinks';
import { getBestPersonProfileLinkId } from '@/lib/personProfileLinkId';
import { buildPersonasTabPagePath } from '@/lib/todaysPlanUtils';
import { logNeoRecommendation } from '@/components/utils/analytics';
import { useTranslation } from '@/components/utils/translations';

const PRIORITY_STYLES = {
  High: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-800 border-amber-200',
  Low: 'bg-gray-50 text-gray-600 border-gray-200',
};

/** Display-only translation keys for the priority value (the value itself stays as sent). */
const PRIORITY_LABEL_KEYS = {
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

function PriorityBadge({ priority }) {
  const { t } = useTranslation();
  if (!priority || !PRIORITY_STYLES[priority]) return null;
  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${PRIORITY_STYLES[priority]}`}
    >
      {t(PRIORITY_LABEL_KEYS[priority])}
    </span>
  );
}

function findPersonByDisplayName(people, displayName) {
  const norm = String(displayName || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!norm || !Array.isArray(people)) return null;

  return (
    people.find((p) => {
      const full = `${String(p?.first_name || '').trim()} ${String(p?.last_name || '').trim()}`
        .trim()
        .toLowerCase();
      if (!full) return false;
      return full === norm || full.includes(norm) || norm.includes(full);
    }) || null
  );
}

function resolveNamedTargetPerson(tp, people, accountId) {
  if (!tp || typeof tp !== 'object') return null;

  const displayName = tp.name ? String(tp.name).trim() : '';
  const directId = getBestPersonProfileLinkId(tp);
  const matchedById = directId
    ? people?.find(
        (p) =>
          String(p?.id || '') === String(directId) ||
          String(p?.person_id || '') === String(directId),
      )
    : null;
  const matchedByName = !matchedById && displayName
    ? findPersonByDisplayName(people, displayName)
    : null;
  const person = matchedById || matchedByName;

  const personId = getBestPersonProfileLinkId(person || tp);
  if (!personId) return null;

  let firstName = String(person?.first_name || tp.first_name || '').trim();
  let lastName = String(person?.last_name || tp.last_name || '').trim();
  if (!firstName && !lastName && displayName) {
    const parts = displayName.split(/\s+/).filter(Boolean);
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ');
  }

  const name =
    displayName ||
    `${firstName} ${lastName}`.trim() ||
    String(tp.full_name || '').trim();
  const roleTitle = String(
    person?.role_title || tp.role_title || tp.type || '',
  ).trim();

  const profilePath = buildChatbotEntityPagePath('person', personId, {
    accountId: person?.account_id || accountId,
  });

  return {
    personId,
    name,
    firstName,
    lastName,
    roleTitle,
    hostedPhotoUrl: person?.hosted_photo_url || tp.hosted_photo_url || null,
    linkedinPhotoUrl: person?.profile_photo_url || tp.profile_photo_url || null,
    profilePath,
  };
}

function TargetPersonaChip({ person }) {
  if (!person?.profilePath || !person?.name) return null;

  return (
    <Link
      to={createPageUrl(person.profilePath)}
      className="flex items-center gap-2.5 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 hover:border-[#00C2C2]/40 hover:bg-[#00C2C2]/5 transition-colors group"
    >
      <PersonAvatar
        personId={person.personId}
        hostedPhotoUrl={person.hostedPhotoUrl}
        linkedinPhotoUrl={person.linkedinPhotoUrl}
        firstName={person.firstName}
        lastName={person.lastName}
        name={person.name}
        size="sm"
      />
      <div className="min-w-0 flex-1 text-left">
        <p className="text-sm font-medium text-[#0A1A2F] truncate group-hover:text-[#00C2C2] transition-colors">
          {person.name}
        </p>
        {person.roleTitle ? (
          <p className="text-xs text-[#5C6B7A] truncate">{person.roleTitle}</p>
        ) : null}
      </div>
      <ChevronRight className="w-4 h-4 text-[#5C6B7A] shrink-0 group-hover:text-[#00C2C2]" aria-hidden />
    </Link>
  );
}

function TargetPersonaPill({ tp, accountId, people }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!tp || typeof tp !== 'object') return null;

  const name = tp.name ? String(tp.name).trim() : '';
  const type = tp.type ? String(tp.type).trim() : '';
  const examples = Array.isArray(tp.examples)
    ? tp.examples.map((e) => String(e || '').trim()).filter(Boolean)
    : [];
  const personasPath = buildPersonasTabPagePath(accountId);

  if (name) {
    const targetPerson = resolveNamedTargetPerson(tp, people, accountId);

    return (
      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#7A8694]">
          {t('acctNbmTarget')}
        </p>
        {targetPerson ? (
          <TargetPersonaChip person={targetPerson} />
        ) : (
          <p className="text-sm text-[#0A1A2F]">
            {name}
            {type ? ` · ${type}` : ''}
          </p>
        )}
      </div>
    );
  }

  if (!type && examples.length === 0) return null;

  return (
    <div className="text-sm space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#7A8694]">
        {t('acctNbmSuggestedRole')}
      </p>
      {type ? <p className="text-sm text-[#0A1A2F]">{type}</p> : null}
      {examples.length > 0 ? (
        <p className="text-xs text-[#7A8694]">
          {t('acctNbmNoNamedContactTypically', { examples: examples.slice(0, 3).join(' · ') })}
        </p>
      ) : (
        <p className="text-xs text-[#7A8694]">{t('acctNbmNoNamedContact')}</p>
      )}
      <p className="text-xs text-[#5C6B7A] italic">
        {t('acctNbmFindOneFirst')}
      </p>
      {personasPath ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs h-7 mt-1"
          onClick={() => navigate(createPageUrl(personasPath))}
        >
          {t('openPersonas')}
        </Button>
      ) : null}
    </div>
  );
}

/** Structured NBM content for the chatbot thread (not shown on Sales Map page). */
export default function NextBestMoveMessage({ nbm, accountId, people, eventTracking }) {
  const { t } = useTranslation();
  const loggedRef = useRef(null);
  const eventKey = accountId ? `nbm-${accountId}` : null;
  const tenantId = eventTracking?.tenantId;
  const userId = eventTracking?.userId;

  useEffect(() => {
    if (!nbm?.move || !tenantId || !userId || !eventKey || loggedRef.current === eventKey) return;
    loggedRef.current = eventKey;

    const persona = nbm.target_persona && typeof nbm.target_persona === 'object'
      ? nbm.target_persona
      : null;

    void logNeoRecommendation({
      tenantId,
      userId,
      accountId,
      accountName: nbm.account_name,
      personId: persona?.person_id || persona?.id,
      personName: persona?.name,
      recommendationType: 'nbm',
      source: 'chatbot',
      metadata: {
        priority: nbm.priority,
      },
    });
  }, [accountId, eventKey, nbm, tenantId, userId]);

  if (!nbm?.move) return null;

  const questions = Array.isArray(nbm.what_to_ask)
    ? nbm.what_to_ask.map((q) => String(q || '').trim()).filter(Boolean)
    : [];
  const persona = nbm.target_persona && typeof nbm.target_persona === 'object'
    ? nbm.target_persona
    : null;

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">
          {t('acctNextBestMove')}
        </p>
        <PriorityBadge priority={nbm.priority} />
      </div>

      <p className="text-sm font-semibold text-[#0A1A2F] leading-snug">{nbm.move}</p>

      {nbm.why ? (
        <p className="text-sm text-[#5C6B7A] leading-relaxed">{nbm.why}</p>
      ) : null}

      {questions.length > 0 ? (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#7A8694] mb-1">
            {t('acctNbmAskNext')}
          </p>
          <ul className="list-disc pl-4 space-y-0.5">
            {questions.map((question) => (
              <li key={question} className="text-sm text-[#0A1A2F] leading-relaxed">
                {question}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <TargetPersonaPill tp={persona} accountId={accountId} people={people} />
    </div>
  );
}
